interface GeneralData {
    isLoan: boolean;
    isTcp: boolean;
    isIgr: boolean;
    creationDate: string;
    myPortfolio: string;
    regions: string | null;
    countries: string | null;
    divisions: string | null;
    pmrClassification: string | null;
    lasDisburmsementInDays: any[];
    age: any[];
    disbursementProjection: any[];
}

interface Composition {
    projects: number;
    operations: number;
    years: number;
    guarantees?: number;
    pbp?: number;
    investment?: number;
    projectTotalCountRegional?: number;
    projectTotalCountNational?: number;
}

interface BidAmount {
    currentAmount: number;
    originalAmount: number;
    disbursementPercent: number;
    undisburstmentPercent: number;
    disbursementAmount: number;
    undisburstmentAmount: number;
    guaranteesAmount?: number;
    pbpAmount?: number;
    pbpInvestment?: number;
}

interface SingleCard {
    pendingLegalValidity: number;
    projectsOverdueDisbursement: number;
}

interface DisbursementAndProjections {
    projectionsDisbursementsYearAmount?: number;
    projectionsDisbursementsYearPercent?: number;
    projectionsWithoutDisbursementsYearAmount?: number;
    projectionsWithoutDisbursementsYearPercent?: number;
}

interface ApprovedProjects {
    aprprovedProjectCount: number;
    aprprovedProjectAmount: number;
    preparationProjectCount: number;
    preparationProjectAmount: number;
}

interface Sector {
    region: string;
    data: Record<string, number>;
}

interface LoanData {
    composition: Composition;
    bidAmount: BidAmount;
    singleCard: SingleCard;
    disbursementAndProjections?: DisbursementAndProjections;
    approvedprojects?: ApprovedProjects;
    sectors: Sector[];
    pmr: any;
    additionalData?: Record<string, any>;
}

interface ResultData {
    generalData: GeneralData;
    loan?: LoanData;
    tcp?: LoanData;
    igr?: LoanData;
}

interface Sector {
    region: string;
    data: Record<string, number>;
    projectCounts?: Record<string, number>;
}

function toNum(v: any, d = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
}

function transformSectors(sectors: any[] = []): Sector[] {
    return sectors.map((sector) => {
        const { amounts, counts } = (sector.divisions ?? []).reduce(
            (acc: { amounts: Record<string, number>; counts: Record<string, number> }, division: any) => {
                const key = division.divisionAcronym;
                acc.amounts[key] = toNum(division.divisionAmount ?? division.divisionAmout);
                acc.counts[key] = toNum(division.divisionProjectCount ?? division.projectsCount ?? 0);
                return acc;
            },
            { amounts: {}, counts: {} }
        );

        return {
            region: sector.sectorAcronym,
            data: amounts,
            projectCounts: counts,
        };
    });
}

function extractGeneralData(data: any): GeneralData {
    return {
        isLoan: data.isLoan,
        isTcp: data.isTcp,
        isIgr: data.isIgr,
        creationDate: data.creationDate,
        myPortfolio: data.myPortfolio,
        regions: data.regions,
        countries: data.countries,
        divisions: data.divisions,
        pmrClassification: data.pmrClassification,
        lasDisburmsementInDays: data.lasDisburmsementInDays,
        age: data.age,
        disbursementProjection: data.disbursementProjection,
    };
}

function transformPmr(pmr: any): any[] {
    if (!pmr || typeof pmr !== 'object') return [];

    const order = ['success', 'succes', 'alert', 'problem', 'na'];
    const used = new Set<string>();
    const result: any[] = [];

    for (const key of order) {
        if (pmr[key] && !used.has(key)) {
            result.push(pmr[key]);
            used.add(key);
        }
    }

    for (const [key, value] of Object.entries(pmr)) {
        if (!used.has(key)) {
            result.push(value);
        }
    }

    return result;
}


function extractLoanData(data: any, type: "loan" | "tcp" | "igr"): LoanData {
    const commonData: LoanData = {
        composition: {
            projects: data.projectTotalCount,
            operations: data.operationTotalCount,
            years: data.averageAge,
            guarantees: data.guarantees,
            pbp: data.pbp,
            investment: data.investment,
        },
        bidAmount: {
            currentAmount: data.idbApprovedAmount_Current,
            originalAmount: data.idbApprovedAmount_Original,
            disbursementPercent: data.disbursementPercent,
            undisburstmentPercent: data.withoutDisbursingPercent,
            disbursementAmount: data.disbursementAmount,
            undisburstmentAmount: data.withoutDisbursingAmount,
            guaranteesAmount: data.guaranteesAmount,
            pbpAmount: data.pbpAmount,
            pbpInvestment: data.investmentAmout,
        },
        singleCard: {
            pendingLegalValidity: data.pendingLegalValidity,
            projectsOverdueDisbursement: data.projectsOverdueDisbursement,
        },
        disbursementAndProjections: {
            projectionsDisbursementsYearAmount: data.projectionsDisbursementsYearAmount,
            projectionsDisbursementsYearPercent: data.projectionsDisbursementsYearPercent,
            projectionsWithoutDisbursementsYearAmount: data.projectionsWithoutDisbursementsYearAmount,
            projectionsWithoutDisbursementsYearPercent: data.projectionsWithoutDisbursementsYearPercent,
        },
        approvedprojects: {
            aprprovedProjectCount: data.approvedprojects?.aprprovedProjectCount ?? 0,
            aprprovedProjectAmount: data.approvedprojects?.aprprovedProjectAmount ?? 0,
            preparationProjectCount: data.approvedprojects?.preparationProjectCount ?? 0,
            preparationProjectAmount: data.approvedprojects?.preparationProjectAmount ?? 0,
        },
        sectors: transformSectors(data.sectors),
        pmr: transformPmr(data.pmr)
    };

    if (type === "tcp") {
        commonData.composition = {
            ...commonData.composition,
            projectTotalCountRegional: data.projectTotalCountRegional,
            projectTotalCountNational: data.projectTotalCountNational,
        };

        // Eliminar executionNat y executionReg del additionalData
        // commonData.additionalData = {
        //     taxonomy: data.taxonomy,
        //     participation: data.participation,
        // };
        commonData.additionalData = {
            taxonomy: data.taxonomy,
            participation: data.participation,
            execution: data.execution,
        };
    }

    if (type === "igr") {
        delete commonData.disbursementAndProjections;
    }

    return commonData;
}

export function normalizeData(data: any): ResultData {
    const result: ResultData = {
        generalData: extractGeneralData(data),
    };

    if (data.isLoan) {
        result.loan = extractLoanData(data.loan, "loan");
    }
    if (data.isTcp) {
        result.tcp = extractLoanData(data.tcp, "tcp");
    }
    if (data.isIgr) {
        result.igr = extractLoanData(data.igr, "igr");
    }

    return result;
}